import { z } from 'zod';
import { uuidSchema } from './common';

/** Zod mirror of the payout-deduction config (see `../payout-deductions.ts`). */
const fixedChargeSchema = z.object({
  enabled: z.boolean(),
  mode: z.enum(['percent', 'flat']),
  value: z.number().min(0),
});

const wtaxBracketSchema = z.object({
  upTo: z.number().positive().nullable(),
  percent: z.number().min(0).max(100),
});

const wtaxConfigSchema = z.object({
  enabled: z.boolean(),
  brackets: z
    .array(wtaxBracketSchema)
    // At most one open-ended (upTo: null) bracket.
    .refine(
      (bs) => bs.filter((b) => b.upTo === null).length <= 1,
      'at most one open-ended bracket (upTo: null) is allowed',
    ),
});

export const payoutMethodDeductionsSchema = z.object({
  service_charge: fixedChargeSchema,
  admin_fee: fixedChargeSchema,
  withholding_tax: wtaxConfigSchema,
});

const payoutMethodSchema = z.object({
  code: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_]+$/, 'code must be lowercase letters, digits, or underscores'),
  label: z.string().min(1).max(80),
  accountLabel: z.string().min(1).max(80),
  accountPlaceholder: z.string().max(120).optional(),
  fields: z
    .array(
      z.object({
        key: z
          .string()
          .min(1)
          .max(40)
          .regex(/^[a-z0-9_]+$/, 'key must be lowercase letters, digits, or underscores'),
        label: z.string().min(1).max(80),
        placeholder: z.string().max(120).optional(),
        required: z.boolean().optional(),
        role: z.enum(['account_holder', 'account_number', 'account_type']).optional(),
      }),
    )
    .refine(
      (fs) => new Set(fs.map((f) => f.key)).size === fs.length,
      'field keys must be unique',
    )
    .optional(),
  // Disbursement-file columns. `procCode` is the bank/processor code the
  // tenant's bank portal expects in the `Proc` column; `defaultAccountType`
  // fills `Type` when no field carries the `account_type` role.
  procCode: z.string().trim().max(16).optional(),
  defaultAccountType: z.string().trim().max(24).optional(),
  enabled: z.boolean(),
  deductions: payoutMethodDeductionsSchema,
});

export const payoutDeductionsConfigSchema = z.object({
  methods: z
    .array(payoutMethodSchema)
    .refine(
      (ms) => new Set(ms.map((m) => m.code)).size === ms.length,
      'payout method codes must be unique',
    ),
  default: payoutMethodDeductionsSchema,
  // Optional global cascade order. Omitted/empty = the legacy math (both fees
  // off gross, then WTAX). `resolvePayoutOrder` tolerates a partial list by
  // appending the missing keys, but reject duplicates here — a repeated key is
  // an editor bug, not a policy anyone means to express.
  order: z
    .array(z.enum(['service_charge', 'admin_fee', 'withholding_tax']))
    .max(3)
    .refine((ks) => new Set(ks).size === ks.length, 'deduction order must not repeat a key')
    .optional(),
});

export type PayoutDeductionsConfigInput = z.infer<typeof payoutDeductionsConfigSchema>;

/**
 * Zod mirror of the ecom payment-methods registry
 * (see `../ecom-payment-methods.ts`). One row = one (currency × settlement)
 * checkout option. We enforce two cross-field constraints here so admins can't
 * shoot themselves in the foot:
 *  - Codes must be unique within the registry.
 *  - `gc`/`coin` methods MUST be settled by `wallet` (manual proof upload
 *    doesn't apply to scrip — there's nothing off-ledger to prove).
 */
const ecomPaymentMethodSchema = z.object({
  code: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_]+$/, 'code must be lowercase letters, digits, or underscores'),
  label: z.string().min(1).max(80),
  currency: z.enum(['cash', 'gc', 'coin']),
  settlement: z.enum(['wallet', 'manual', 'gateway', 'cod_manual']),
  accountLabel: z.string().max(160).optional(),
  instructions: z.string().max(1000).optional(),
  logoKey: z.string().max(512).optional(),
  qrKey: z.string().max(512).optional(),
  // For settlement='gateway': which online provider drives this method, plus an
  // optional pre-selected channel (Dragonpay ProcId / iPay88 PaymentId).
  gatewayProvider: z.enum(['dragonpay', 'ipay88', 'stub']).optional(),
  gatewayPaymentId: z.string().max(64).optional(),
  // For settlement='cod_manual': the courier id used to price shipping at
  // checkout (no courier is chosen during the absent online-payment step). Zod
  // can only enforce presence; existence/enabled/COD-eligibility is validated
  // server-side against the active shipping config in the tenant route.
  defaultCourierId: z.string().min(1).max(64).optional(),
  enabled: z.boolean(),
});

export const ecomPaymentMethodsConfigSchema = z.object({
  methods: z
    .array(ecomPaymentMethodSchema)
    .refine(
      (ms) => new Set(ms.map((m) => m.code)).size === ms.length,
      'ecom payment method codes must be unique',
    )
    .refine(
      (ms) => ms.every((m) => m.currency === 'cash' || m.settlement === 'wallet'),
      'gc/coin methods must use the wallet settlement (manual proof + online gateways are cash-only)',
    )
    .refine(
      (ms) => ms.every((m) => m.settlement !== 'cod_manual' || !!m.defaultCourierId),
      'Cash on Delivery methods require a default courier',
    ),
});

export type EcomPaymentMethodsConfigInput = z.infer<typeof ecomPaymentMethodsConfigSchema>;

const posPaymentMethodSchema = z.object({
  code: z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_]+$/, 'code must be lowercase letters, digits, or underscores'),
  label: z.string().min(1).max(80),
  tenderKind: z.enum(['cash', 'exact', 'wallet']),
  currency: z.enum(['cash', 'gc', 'coin']),
  enabled: z.boolean(),
});

export const posPaymentMethodsConfigSchema = z.object({
  methods: z
    .array(posPaymentMethodSchema)
    .refine(
      (ms) => new Set(ms.map((m) => m.code)).size === ms.length,
      'POS payment method codes must be unique',
    )
    // Only a wallet tender may use a non-cash currency; cash/exact tenders are
    // always cash-denominated. A wallet tender MAY debit the cash wallet (the
    // buyer pays the normal cash total from their cash balance).
    .refine(
      (ms) => ms.every((m) => m.tenderKind === 'wallet' || m.currency === 'cash'),
      'cash/exact methods must use the cash currency',
    ),
});

export type PosPaymentMethodsConfigInput = z.infer<typeof posPaymentMethodsConfigSchema>;

/** Body for `POST /payouts/requests/preview` — the live net-payout quote. */
export const payoutPreviewSchema = z.object({
  slotId: uuidSchema,
  amount: z
    .string()
    .regex(/^\d+(\.\d{1,4})?$/, 'amount must be a decimal with up to 4 fractional digits'),
  method: z.string().min(1).max(64),
});

export type PayoutPreviewInput = z.infer<typeof payoutPreviewSchema>;
